import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Search, X, Eye, Pencil, Phone, Trash2, ShoppingCart, UserPlus, Receipt } from 'lucide-react';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

const paymentStatusColors = {
  paid_full: 'bg-green-100 text-green-800',
  on_schedule: 'bg-blue-100 text-blue-800',
  behind: 'bg-amber-100 text-amber-800',
  defaulted: 'bg-red-100 text-red-800',
};
const paymentStatusLabels = { paid_full: 'Paid in Full', on_schedule: 'On Schedule', behind: 'Behind', defaulted: 'Defaulted' };

const formatNaira = (amount) => {
  if (!amount && amount !== 0) return '--';
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
};

// Edit form is restricted to personal/contact info only (purchase fields are managed via Sales).
const emptyEdit = {
  full_name: '', phone: '', email: '', alt_phone: '', address: '', date_of_birth: '', occupation: '',
  next_of_kin_name: '', next_of_kin_relationship: '', next_of_kin_phone: '',
  client_status: 'active', notes: '', assigned_plot_id: '',
  total_price: '', amount_paid: '', payment_plan: '', number_of_plots: '',
  property_estate_name: '', property_location: '',
};

const BOClients = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useBOAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ ...emptyEdit });
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, client: null });
  const [availablePlots, setAvailablePlots] = useState([]);
  const formRef = useRef(null);

  useEffect(() => {
    if (editClient && formRef.current) {
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [editClient]);

  useEffect(() => { fetchClients(); }, []);

  // When arriving with ?edit=<client_id>, fetch the client and open the editor automatically.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    (async () => {
      try {
        const r = await boAPI.getClient(editId);
        if (r?.data?.id) handleEdit(r.data);
      } catch { /* ignore */ }
      // Clear the query param so refresh doesn't reopen the editor.
      const np = new URLSearchParams(searchParams); np.delete('edit'); setSearchParams(np, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClients = async (search = '') => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterPayment) params.payment_status = filterPayment;
      const res = await boAPI.getClients(params);
      setClients(res.data.clients || []);
    } catch (e) { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClients(searchQuery); }, [filterStatus, filterPayment]);

  const handleSearch = (e) => { e.preventDefault(); fetchClients(searchQuery); };

  const handleEdit = async (c) => {
    setEditClient(c);
    setForm({
      full_name: c.full_name || '', phone: c.phone || '', email: c.email || '', alt_phone: c.alt_phone || '',
      address: c.address || '', date_of_birth: c.date_of_birth || '', occupation: c.occupation || '',
      next_of_kin_name: c.next_of_kin_name || '', next_of_kin_relationship: c.next_of_kin_relationship || '', next_of_kin_phone: c.next_of_kin_phone || '',
      client_status: c.client_status || 'active', notes: c.notes || '',
      assigned_plot_id: c.assigned_plot_id || '',
      total_price: c.total_price || '', amount_paid: c.amount_paid || '', payment_plan: c.payment_plan || '',
      number_of_plots: c.number_of_plots || '',
      property_estate_name: c.property_estate_name || '',
      property_location: c.property_location || '',
    });
    // Load plots so admin can pick a different one (include the client's current plot even if sold)
    try {
      const r = await boAPI.getPlots({ limit: 5000 });
      const list = r.data.plots || [];
      const filtered = list.filter(p => p.availability_status === 'available' || p.id === c.assigned_plot_id);
      setAvailablePlots(filtered);
    } catch { /* non-fatal */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) { toast.error('Name and phone are required'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Coerce numeric fields
      payload.total_price = payload.total_price === '' || payload.total_price == null ? null : parseFloat(payload.total_price) || 0;
      payload.amount_paid = payload.amount_paid === '' || payload.amount_paid == null ? null : parseFloat(payload.amount_paid) || 0;
      payload.number_of_plots = payload.number_of_plots === '' || payload.number_of_plots == null ? null : parseInt(payload.number_of_plots, 10) || 0;
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (editClient?.id) {
        await boAPI.updateClient(editClient.id, payload);
        toast.success('Client updated');
      } else {
        await boAPI.createClient(payload);
        toast.success('Client added');
      }
      setEditClient(null);
      setForm({ ...emptyEdit });
      fetchClients(searchQuery);
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Failed to save client'); }
    finally { setSubmitting(false); }
  };

  const askDelete = (c) => setConfirm({ open: true, client: c });
  const cancelDelete = () => setConfirm({ open: false, client: null });
  const confirmDelete = async () => {
    if (!confirm.client) return;
    try {
      await boAPI.deleteClient(confirm.client.id);
      toast.success('Client deleted');
      cancelDelete();
      fetchClients(searchQuery);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete client');
    }
  };

  const isReadOnly = role === 'accountant';

  if (loading) {
    return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;
  }

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-clients-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Clients</h1>
            <p className="text-neutral-500 mt-1">{clients.length} client{clients.length !== 1 ? 's' : ''} on file. You can add a client on its own — plot &amp; payment info are optional and can be attached later.</p>
          </div>
          {!isReadOnly && (
            <div className="flex gap-2 flex-wrap">
              <Button onClick={async () => {
                setEditClient({ id: null });
                setForm({ ...emptyEdit });
                try {
                  const r = await boAPI.getPlots({ limit: 5000 });
                  setAvailablePlots((r.data.plots || []).filter(p => p.availability_status === 'available'));
                } catch { /* non-fatal */ }
              }} variant="outline" className="rounded-xl" data-testid="new-client-btn">
                <UserPlus className="h-4 w-4 mr-2" /> New Client/Sale
              </Button>
              <Button onClick={() => navigate('/backoffice/sales')} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="record-sale-from-clients-btn">
                <ShoppingCart className="h-4 w-4 mr-2" /> Record Sale
              </Button>
            </div>
          )}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input placeholder="Search by name or phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-xl" data-testid="client-search" />
          </form>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="filter-status">
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="completed">Completed</option>
          </select>
          <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="filter-payment">
            <option value="">All Payments</option>
            <option value="paid_full">Paid in Full</option>
            <option value="on_schedule">On Schedule</option>
            <option value="behind">Behind</option>
            <option value="defaulted">Defaulted</option>
          </select>
        </div>

        {/* Edit Form */}
        {editClient && (
          <Card ref={formRef} className="border-primary-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{editClient?.id ? 'Edit Client/Sale' : 'New Client/Sale'}</CardTitle>
                <CardDescription>{editClient?.id
                  ? 'Update personal, next-of-kin details, and optionally reassign the plot/block. Payment info is managed from the Sales page.'
                  : 'Record client details now. Plot, block and payment info are optional — you can attach them later by editing this client.'}</CardDescription>
              </div>
              <button onClick={() => setEditClient(null)} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div><Label>Full Name *</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="client-form-name" /></div>
                <div><Label>Phone *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-xl" data-testid="client-form-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div><Label>Alt Phone</Label><Input value={form.alt_phone} onChange={(e) => setForm({ ...form, alt_phone: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div><Label>Occupation</Label><Input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div className="sm:col-span-2 lg:col-span-3"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 rounded-xl" /></div>

                <div className="sm:col-span-2 lg:col-span-3 border-t pt-4 mt-2"><h3 className="font-semibold text-neutral-800">Next of Kin (Optional)</h3></div>
                <div><Label>Next of Kin Name</Label><Input value={form.next_of_kin_name} onChange={(e) => setForm({ ...form, next_of_kin_name: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div><Label>Relationship</Label><Input value={form.next_of_kin_relationship} onChange={(e) => setForm({ ...form, next_of_kin_relationship: e.target.value })} placeholder="e.g., Spouse, Sibling" className="mt-1.5 rounded-xl" /></div>
                <div><Label>Next of Kin Phone</Label><Input value={form.next_of_kin_phone} onChange={(e) => setForm({ ...form, next_of_kin_phone: e.target.value })} className="mt-1.5 rounded-xl" /></div>

                <div className="sm:col-span-2 lg:col-span-3 border-t pt-4 mt-2"><h3 className="font-semibold text-neutral-800">Property Info <span className="text-xs font-normal text-neutral-500">(optional — shows on receipts)</span></h3><p className="text-xs text-neutral-500 mt-1">Type the estate name and location. These appear on all receipts for this client. If a real plot is attached later, its details take priority and these fill any blanks.</p></div>
                <div className="sm:col-span-2">
                  <Label>Estate Name</Label>
                  <Input value={form.property_estate_name} onChange={(e) => setForm({ ...form, property_estate_name: e.target.value })} placeholder='e.g., "Royal Gardens Estate"' className="mt-1.5 rounded-xl" data-testid="client-property-estate-name" />
                </div>
                <div>
                  <Label>Estate Location</Label>
                  <Input value={form.property_location} onChange={(e) => setForm({ ...form, property_location: e.target.value })} placeholder='e.g., "Asaba, Delta State"' className="mt-1.5 rounded-xl" data-testid="client-property-location" />
                </div>

                <div className="sm:col-span-2 lg:col-span-3 border-t pt-4 mt-2"><h3 className="font-semibold text-neutral-800">Plot Assignment <span className="text-xs font-normal text-neutral-500">(optional)</span></h3><p className="text-xs text-neutral-500 mt-1">Record how many plots the client bought. You can attach specific plots now or leave blank and attach later.</p></div>
                <div>
                  <Label>Number of Plots Bought</Label>
                  <Input type="number" min="0" step="1" value={form.number_of_plots} onChange={(e) => setForm({ ...form, number_of_plots: e.target.value })} placeholder="e.g., 1" className="mt-1.5 rounded-xl" data-testid="client-number-of-plots" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Attach a Specific Plot <span className="text-xs font-normal text-neutral-500">(optional)</span></Label>
                  <select
                    value={form.assigned_plot_id || ''}
                    onChange={(e) => setForm({ ...form, assigned_plot_id: e.target.value })}
                    className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                    data-testid="client-assigned-plot"
                  >
                    <option value="">— No plot assigned —</option>
                    {availablePlots.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.estate_name}{p.location_block ? ` · ${p.location_block}` : ''} · {p.plot_number}
                        {p.id === editClient?.assigned_plot_id ? ' (current)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2 lg:col-span-3 border-t pt-4 mt-2"><h3 className="font-semibold text-neutral-800">Payment Details <span className="text-xs font-normal text-neutral-500">(optional)</span></h3><p className="text-xs text-neutral-500 mt-1">Record the property price, amount paid so far, and payment plan. Balance is calculated automatically.</p></div>
                <div>
                  <Label>Total Price (₦)</Label>
                  <Input type="number" min="0" step="1" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} placeholder="0" className="mt-1.5 rounded-xl" data-testid="client-total-price" />
                </div>
                <div>
                  <Label>Amount Paid (₦)</Label>
                  <Input type="number" min="0" step="1" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} placeholder="0" className="mt-1.5 rounded-xl" data-testid="client-amount-paid" />
                </div>
                <div>
                  <Label>Payment Plan</Label>
                  <select value={form.payment_plan || ''} onChange={(e) => setForm({ ...form, payment_plan: e.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="client-payment-plan">
                    <option value="">— Not set —</option>
                    <option value="full">Outright (Full Payment)</option>
                    <option value="3_months">3 Months Instalment</option>
                    <option value="6_months">6 Months Instalment</option>
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  {(() => {
                    const tp = parseFloat(form.total_price) || 0;
                    const ap = parseFloat(form.amount_paid) || 0;
                    const bal = Math.max(0, tp - ap);
                    const paidFull = tp > 0 && ap >= tp;
                    if (tp === 0 && ap === 0) return null;
                    return (
                      <div className={`rounded-xl p-3 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${paidFull ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="client-balance-preview">
                        <div className="flex flex-wrap gap-4">
                          <span>Price: <strong>{new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(tp)}</strong></span>
                          <span>Paid: <strong className="text-emerald-700">{new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(ap)}</strong></span>
                          <span>Balance: <strong className={paidFull ? 'text-emerald-700' : 'text-red-600'}>{new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(bal)}</strong></span>
                        </div>
                        {paidFull && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-emerald-600 text-white">PAID IN FULL</span>}
                      </div>
                    );
                  })()}
                </div>

                <div className="sm:col-span-2 lg:col-span-3 border-t pt-4 mt-2"><h3 className="font-semibold text-neutral-800">Status &amp; Notes</h3></div>
                <div>
                  <Label>Client Status</Label>
                  <select value={form.client_status} onChange={(e) => setForm({ ...form, client_status: e.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                    <option value="active">Active</option><option value="inactive">Inactive</option><option value="completed">Completed</option>
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-3"><Label>Notes</Label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm resize-none" /></div>
                <div className="sm:col-span-2 lg:col-span-3 flex gap-3 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditClient(null)} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="client-form-submit">{submitting ? 'Saving...' : (editClient?.id ? 'Update Client' : 'Save Client')}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Clients Table */}
        <Card className="border-neutral-border shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead>
                <tr className="border-b bg-neutral-50">
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Client</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden md:table-cell">Plot</th>
                  <th className="text-center px-4 py-3 font-medium text-neutral-600 hidden md:table-cell">Plots</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Total Price</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600 hidden sm:table-cell">Paid</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600 hidden sm:table-cell">Balance</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Payment</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-neutral-50 transition-colors" data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{c.full_name}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-neutral-600">{c.plot_info ? `${c.plot_info.plot_number}${c.plot_info.location_block ? ' · ' + c.plot_info.location_block : ''} · ${c.plot_info.estate_name}` : '--'}</td>
                    <td className="px-4 py-3 text-center hidden md:table-cell font-medium">{c.number_of_plots || (c.plot_info ? 1 : 0)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatNaira(c.total_price)}</td>
                    <td className="px-4 py-3 text-right text-green-700 hidden sm:table-cell">{formatNaira(c.amount_paid)}</td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium hidden sm:table-cell">{formatNaira(c.balance_remaining)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${paymentStatusColors[c.payment_status] || 'bg-neutral-100'}`}>
                        {paymentStatusLabels[c.payment_status] || c.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {(c.amount_paid || 0) > 0 && (
                          <button onClick={async () => {
                            try {
                              const r = await boAPI.getPayments({ client_id: c.id, limit: 5 });
                              const p = (r.data.payments || [])[0];
                              if (p) navigate(`/backoffice/receipt/${p.id}`);
                              else toast.error('No receipt found for this client');
                            } catch { toast.error('Failed to fetch receipt'); }
                          }} className="p-2 rounded-lg hover:bg-neutral-100 text-primary-700" title="Latest Receipt" data-testid={`client-receipt-${c.id}`}><Receipt className="h-4 w-4" /></button>
                        )}
                        <button onClick={() => navigate(`/backoffice/clients/${c.id}`)} className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-600" title="View" data-testid={`view-client-${c.id}`}><Eye className="h-4 w-4" /></button>
                        {!isReadOnly && <button onClick={() => handleEdit(c)} className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-600" title="Edit" data-testid={`edit-client-${c.id}`}><Pencil className="h-4 w-4" /></button>}
                        {!isReadOnly && <button onClick={() => askDelete(c)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Delete" data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {clients.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-neutral-500">No clients found. Record a sale to add a client.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => !o && cancelDelete()}
        title="Delete this client?"
        description={confirm.client ? `This permanently removes ${confirm.client.full_name}, all their sales, payments, document collections, and frees their plots. This cannot be undone.` : ''}
        confirmLabel="Delete client"
        onConfirm={confirmDelete}
      />
    </BOLayout>
  );
};

export default BOClients;
